import {
	API_EXCHANGE_DATA_KEYS,
	CURRENCY_FO,
	CURRENCY_INSTRUMENTS,
	EQUITY_FO_INSTRUMENT,
	EQUITY_MARKET_SEGMENTS,
	KEY_STATS_VARIANTS,
	OPTIONS_SCRIPS,
	SCRIP_DETAILS_TAB_VARIANTS,
	SCRIP_INSTRUMENTS,
	SCRIP_LABEL_ID_MAPPER,
	SCRIP_TAB_CTA_VARIANTS,
	SELECTED_EXCHANGE_RADIO_OPTIONS,
} from '../constants/scripDetail';

import commonBaseService from './commonBaseService';
/*
	The below function calculates the premiun/discount for the future scrips
    input:
    spotPrice: price of the underlying scrip
    future price: price of future scrip
	value in decimal format.
	Example:
	input: 1234, 1334
	output: (-10)

	input: 1334, 1232
	output: (10)
*/
import { get, sortedIndexBy, isEmpty, findIndex, filter } from 'lodash-es';
import { POPULAR_OPTION_CHAIN_OPTIONS } from '../constants/equity/optionChain';

const calculatePremiunDiscount = (
	future_price,
	spot_price,
	isCurrencyScrip = false,
) => {
	const futureTradedPrice = Number(future_price);
	const underlyingTradedPrice = Number(spot_price);
	const decimalPlaces = isCurrencyScrip ? 4 : 2;
	if (
		!commonBaseService.isValidNumber(futureTradedPrice) ||
		!commonBaseService.isValidNumber(underlyingTradedPrice) ||
		futureTradedPrice === 0 ||
		underlyingTradedPrice === 0
	) {
		return '-';
	}

	const premium = futureTradedPrice - underlyingTradedPrice;
	const formatedPremium = commonBaseService.formatNumber(premium, {
		decimalPlaces,
		addSign: true,
	});
	if (commonBaseService.isValidNumber(premium)) {
		return formatedPremium;
	}

	return '-';
};

/*
	The below function calculates the premiun/discount percentage for the future scrips
    input:
    spotPrice: price of the underlying scrip
    future price: price of future scrip
	value in decimal format.
	Example:

	input: 1334, 1232
	output: (0.74%)
*/

const calculatePremiunDiscountPerc = (future_price, spot_price) => {
	if (
		commonBaseService.isEmptyOrUndefined(future_price) ||
		commonBaseService.isEmptyOrUndefined(spot_price)
	) {
		return '-';
	}
	const futureTradedPrice = Number(future_price);
	const underlyingTradedPrice = Number(spot_price);
	if (
		!commonBaseService.isValidNumber(futureTradedPrice) ||
		!commonBaseService.isValidNumber(underlyingTradedPrice) ||
		futureTradedPrice === 0 ||
		underlyingTradedPrice === 0
	) {
		return '-';
	}

	const premium = futureTradedPrice - underlyingTradedPrice;
	const premiumPercentage = (premium / underlyingTradedPrice) * 100;
	if (commonBaseService.isValidNumber(premiumPercentage)) {
		const formatedPremiumPerc = commonBaseService.formatNumber(
			premiumPercentage,
			{
				retainNegativeSign: false,
				formatAsPercentage: true,
			},
		);
		return `(${formatedPremiumPerc})`;
	}

	return '-';
};

/*
	The below function formats the string for showing last traded quantity for live snapshot
    input:
    last_traded_quantity: last traded quantity of scrip
    last_traded_price: price of  scrip
	Example:

	input: 1334, 122.23
	output: 1334@122.23
*/

const formatLastTradedQuantity = (last_traded_quantity, last_traded_price) => {
	if (
		commonBaseService.isEmptyOrUndefined(last_traded_price) ||
		commonBaseService.isEmptyOrUndefined(last_traded_quantity)
	) {
		return '-';
	}

	return `${last_traded_quantity} @ ${last_traded_price}`;
};

const mapScripLabelToTabVariant = (scripLabelId, isIndexScrip) => {
	if (isIndexScrip) {
		return SCRIP_DETAILS_TAB_VARIANTS.INDEX;
	} else {
		switch (scripLabelId) {
			case SCRIP_LABEL_ID_MAPPER.equity:
			case SCRIP_LABEL_ID_MAPPER.reits:
				return SCRIP_DETAILS_TAB_VARIANTS.EQUITY;
			case SCRIP_LABEL_ID_MAPPER.etf:
			case SCRIP_LABEL_ID_MAPPER.ppe:
			case SCRIP_LABEL_ID_MAPPER.ps:
			case SCRIP_LABEL_ID_MAPPER.bonds:
			case SCRIP_LABEL_ID_MAPPER.invits:
			case SCRIP_LABEL_ID_MAPPER.gb:
			case SCRIP_LABEL_ID_MAPPER.gs:
			case SCRIP_LABEL_ID_MAPPER.sdl:
			case SCRIP_LABEL_ID_MAPPER.tb:
				return SCRIP_DETAILS_TAB_VARIANTS.ETF;
			case SCRIP_LABEL_ID_MAPPER.warrants:
			case SCRIP_LABEL_ID_MAPPER.sme:
				return SCRIP_DETAILS_TAB_VARIANTS['EQUITY-T2'];
			case SCRIP_LABEL_ID_MAPPER.if:
			case SCRIP_LABEL_ID_MAPPER.sf:
			case SCRIP_LABEL_ID_MAPPER.cf:
			case SCRIP_LABEL_ID_MAPPER.io:
			case SCRIP_LABEL_ID_MAPPER.co:
			case SCRIP_LABEL_ID_MAPPER.so:
			case SCRIP_LABEL_ID_MAPPER.ico:
			case SCRIP_LABEL_ID_MAPPER.ipo:
			case SCRIP_LABEL_ID_MAPPER.sco:
			case SCRIP_LABEL_ID_MAPPER.spo:
			case SCRIP_LABEL_ID_MAPPER.cco:
			case SCRIP_LABEL_ID_MAPPER.cpo:
				return SCRIP_DETAILS_TAB_VARIANTS.FO;
			default:
				return SCRIP_DETAILS_TAB_VARIANTS.EQUITY;
		}
	}
};

const mapMarketSegmentToId = (marketSegement) => {
	switch (marketSegement) {
		case EQUITY_MARKET_SEGMENTS.BSE_EQ:
			return 3;
		case EQUITY_MARKET_SEGMENTS.NSE_EQ:
			return 1;
		case EQUITY_MARKET_SEGMENTS.NSE_DERIVATIVES:
		case EQUITY_MARKET_SEGMENTS.NSE_FO:
			return 2;
		case EQUITY_MARKET_SEGMENTS.NSE_CUR:
		case EQUITY_MARKET_SEGMENTS.NSECDS:
		case EQUITY_MARKET_SEGMENTS.NSE_CURRENCY:
			return 13;
		default:
			return null;
	}
};

const mapExchangeToApiResponseKey = (selectedExchange) => {
	switch (selectedExchange) {
		case SELECTED_EXCHANGE_RADIO_OPTIONS.NSE:
			return API_EXCHANGE_DATA_KEYS.NSE;
		case SELECTED_EXCHANGE_RADIO_OPTIONS.BSE:
			return API_EXCHANGE_DATA_KEYS.BSE;
		default:
			return null;
	}
};

const mapEquitySegmentToSelectedExchange = (equitySegment) => {
	switch (equitySegment) {
		case EQUITY_MARKET_SEGMENTS.BSE_EQ:
			return SELECTED_EXCHANGE_RADIO_OPTIONS.BSE;
		case EQUITY_MARKET_SEGMENTS.NSE_EQ:
		case EQUITY_MARKET_SEGMENTS.NSE_FO:
		case EQUITY_MARKET_SEGMENTS.NSECDS:
		case EQUITY_MARKET_SEGMENTS.NSE_CURRENCY:
		case EQUITY_MARKET_SEGMENTS.NSE_DERIVATIVES:
		case EQUITY_MARKET_SEGMENTS.NSE_CUR:
			return SELECTED_EXCHANGE_RADIO_OPTIONS.NSE;
	}
};

const isIndexScrip = (scripDetails) => {
	const isIndex = get(scripDetails, 'is_index', 0);
	return Number(isIndex) === 1;
};

const isCurrencyScrip = (instrument) => {
	return CURRENCY_INSTRUMENTS.includes(instrument);
};
const isCurrencyFoScrip = (instrument) => {
	return CURRENCY_FO.includes(instrument);
};
const isEquityFOScrip = (instrument) => {
	return EQUITY_FO_INSTRUMENT.includes(instrument);
};
const isEquityScrip = (instrument) => {
	return SCRIP_INSTRUMENTS.EQUITY === instrument;
};

const ETF_SCRIP_LABEL_ID = [
	SCRIP_LABEL_ID_MAPPER.etf,
	SCRIP_LABEL_ID_MAPPER.ppe,
	SCRIP_LABEL_ID_MAPPER.ps,
	SCRIP_LABEL_ID_MAPPER.bonds,
	SCRIP_LABEL_ID_MAPPER.invits,
	SCRIP_LABEL_ID_MAPPER.gb,
	SCRIP_LABEL_ID_MAPPER.gs,
	SCRIP_LABEL_ID_MAPPER.sdl,
	SCRIP_LABEL_ID_MAPPER.tb,
];
const isETFScrip = (scripData) => {
	const scripLabelId = get(scripData, 'scrip_label_id', '');
	return ETF_SCRIP_LABEL_ID.includes(scripLabelId);
};
const createLiveSnapshotVariant = (tabVariant) => {
	switch (tabVariant) {
		case SCRIP_DETAILS_TAB_VARIANTS.EQUITY:
		case SCRIP_DETAILS_TAB_VARIANTS['EQUITY-T2']:
			return KEY_STATS_VARIANTS.STOCKS;
		case SCRIP_DETAILS_TAB_VARIANTS.ETF:
			return KEY_STATS_VARIANTS.ETF;
		case SCRIP_DETAILS_TAB_VARIANTS.FO:
			return KEY_STATS_VARIANTS.FUTURES;
		case SCRIP_DETAILS_TAB_VARIANTS.INDEX:
			return KEY_STATS_VARIANTS.INDEX;
	}
};

const mapApiSegmentToEquitySegment = (apiSegement) => {
	switch (Number(apiSegement)) {
		case 8:
			return EQUITY_MARKET_SEGMENTS.BSE_EQ;
		case 1:
			return EQUITY_MARKET_SEGMENTS.NSE_EQ;
		case 2:
			return EQUITY_MARKET_SEGMENTS.NSE_FO;
		case 2048:
			return EQUITY_MARKET_SEGMENTS.NSE_CUR;
		default:
			return null;
	}
};

const mapEquitySegmentToApiSegment = (equitySegment) => {
	switch (equitySegment) {
		case EQUITY_MARKET_SEGMENTS.BSE_EQ:
			return 8;
		case EQUITY_MARKET_SEGMENTS.NSE_EQ:
			return 1;
		case EQUITY_MARKET_SEGMENTS.NSE_CUR:
			return 2048;
		case EQUITY_MARKET_SEGMENTS.NSE_DERIVATIVES:
		case EQUITY_MARKET_SEGMENTS.NSE_FO:
			return 2;
		default:
			return null;
	}
};

const calculateTabCtaVariant = ({ tabVariant, userFnoData, isFO, isCURR }) => {
	switch (tabVariant) {
		case SCRIP_DETAILS_TAB_VARIANTS.EQUITY:
		case SCRIP_DETAILS_TAB_VARIANTS['EQUITY-T2']:
		case SCRIP_DETAILS_TAB_VARIANTS.ETF:
			return SCRIP_TAB_CTA_VARIANTS.BUY_SELL;
		case SCRIP_DETAILS_TAB_VARIANTS.FO:
			return foCTAVariant({ userFnoData, isFO, isCURR });
		case SCRIP_DETAILS_TAB_VARIANTS.INDEX:
			return SCRIP_TAB_CTA_VARIANTS.OPTION_CHAIN;
		default:
			return null;
	}
};

const foCTAVariant = ({ userFnoData, isFO, isCURR }) => {
	const { isNseFO, isNseCUR } = userFnoData;

	if (isNseFO && isNseCUR) {
		return SCRIP_TAB_CTA_VARIANTS.BUY_SELL;
	} else if (isNseFO && !isNseCUR) {
		if (isFO) {
			return SCRIP_TAB_CTA_VARIANTS.BUY_SELL;
		} else if (isCURR) {
			return SCRIP_TAB_CTA_VARIANTS.ACTIVATE_FO;
		}
	} else if (!isNseFO && isNseCUR) {
		if (isFO) {
			return SCRIP_TAB_CTA_VARIANTS.ACTIVATE_FO;
		} else if (isCURR) {
			return SCRIP_TAB_CTA_VARIANTS.BUY_SELL;
		}
	} else if (!isNseFO && !isNseCUR) {
		return SCRIP_TAB_CTA_VARIANTS.ACTIVATE_FO;
	}
};

const foTabCTAVariant = ({ userFnoData, isFO, isCURR }) => {
	const { isNseFO, isNseCUR } = userFnoData;

	if (isFO && isNseFO) {
		return SCRIP_TAB_CTA_VARIANTS.OPTION_CHAIN;
	} else if (isCURR && isNseCUR) {
		return SCRIP_TAB_CTA_VARIANTS.OPTION_CHAIN;
	} else if (isNseFO) {
		return SCRIP_TAB_CTA_VARIANTS.OPTION_CHAIN;
	} else {
		return SCRIP_TAB_CTA_VARIANTS.ACTIVATE_FO;
	}
};

const calculateFoTabCtaVariant = ({
	tabVariant,
	userFnoData,
	isFO,
	isCURR,
}) => {
	switch (tabVariant) {
		case SCRIP_DETAILS_TAB_VARIANTS.EQUITY:
		case SCRIP_DETAILS_TAB_VARIANTS['EQUITY-T2']:
		case SCRIP_DETAILS_TAB_VARIANTS.ETF:
		case SCRIP_DETAILS_TAB_VARIANTS.FO:
			return foTabCTAVariant({ userFnoData, isFO, isCURR });
		case SCRIP_DETAILS_TAB_VARIANTS.INDEX:
			return SCRIP_TAB_CTA_VARIANTS.OPTION_CHAIN;
		default:
			return null;
	}
};

const isFoActivated = (enabledExchanges) => {
	return enabledExchanges.includes(EQUITY_MARKET_SEGMENTS.NSE_FO);
};

const showOptionChainIcon = (tabVariant) => {
	switch (tabVariant) {
		case SCRIP_DETAILS_TAB_VARIANTS.EQUITY:
		case SCRIP_DETAILS_TAB_VARIANTS['EQUITY-T2']:
		case SCRIP_DETAILS_TAB_VARIANTS.ETF:
		case SCRIP_DETAILS_TAB_VARIANTS.INDEX:
			return false;
		case SCRIP_DETAILS_TAB_VARIANTS.FO:
			return true;
		default:
			return false;
	}
};

const isOptionScrip = (instrument) => OPTIONS_SCRIPS.includes(instrument);

const isUnderlyingData = (instrument) =>
	SCRIP_INSTRUMENTS.EQUITY !== instrument;

const VALUATION_ID = [
	SCRIP_LABEL_ID_MAPPER.equity,
	SCRIP_LABEL_ID_MAPPER.reits,
	SCRIP_LABEL_ID_MAPPER.sme,
];
const isValuationVisible = (scripLabelId) => {
	return VALUATION_ID.includes(scripLabelId);
};

const shouldShowBuySellCta = ({
	scripType = '',
	isIndexScrip = false,
	isFO = false,
	isCURR = false,
	userFnoData = {},
}) => {
	const { isNseFO, isNseCUR } = userFnoData;
	switch (scripType) {
		case SCRIP_INSTRUMENTS.FUTURES:
		case SCRIP_INSTRUMENTS.FUTURES_INDEX:
		case SCRIP_INSTRUMENTS.OPTIONS:
		case SCRIP_INSTRUMENTS.OPTION_INDEX:
			return isFO && isNseFO;
		case SCRIP_INSTRUMENTS.FUTURES_CURRENCY:
		case SCRIP_INSTRUMENTS.OPTIONS_CURRENCY:
			return isCURR && isNseCUR;
		case SCRIP_INSTRUMENTS.EQUITY:
			return !isIndexScrip;
		default:
			return false;
	}
};

const mapScripDetailsToLegacyWaveScripDetails = ({
	symbol,
	scrip_key,
	token,
	instrument_name,
	exchange,
}) => {
	return {
		sSymbol: symbol,
		scrip_key: scrip_key,
		nToken: token,
		sInstrumentName: instrument_name,
		exchange_name: exchange,
	};
};

const formatMCap = (mCap) => {
	if (
		commonBaseService.isEmptyOrUndefined(mCap) ||
		!commonBaseService.isValidNumber(mCap)
	) {
		return '-';
	}

	let mCapNumber = Number(mCap);
	let multiplierSymbol = '';
	const croreMultiplier = Math.pow(10, 7);
	const lakhMultiplier = Math.pow(10, 5);
	const thousandMultiplier = Math.pow(10, 3);
	if (mCapNumber > croreMultiplier) {
		multiplierSymbol = 'Cr';
		mCapNumber = mCapNumber / croreMultiplier;
	}

	let formattedMCap = commonBaseService.formatNumber(mCapNumber, {
		decimalPlaces: mCap > croreMultiplier ? 2 : 0,
		retainNegativeSign: false,
	});
	formattedMCap = !commonBaseService.isEmptyOrUndefined(multiplierSymbol)
		? `${formattedMCap} ${multiplierSymbol}`
		: formattedMCap;
	return formattedMCap;
};

const userFutureCurrency = (enabledExchanges) => {
	return {
		isNseFO: enabledExchanges.includes(EQUITY_MARKET_SEGMENTS.NSE_FO),
		isNseCUR: enabledExchanges.includes(EQUITY_MARKET_SEGMENTS.NSE_CUR),
	};
};

const getScripDetailForeignKey = (scripSegment, scripToken) => {
	return `${scripSegment}_${scripToken}`;
};
const isTopOptionChainVisible = (selectedScrip = {}) => {
	const isIndex = isIndexScrip(selectedScrip);
	const isEquityFoScrip = isEquityFOScrip(selectedScrip?.instrument_type);
	const isCurrencyScrip = isCurrencyFoScrip(selectedScrip?.instrument_type);
	// No need to show in case of equity scrip
	if (
		selectedScrip?.instrument_type === 'EQUITIES' &&
		selectedScrip?.fo_exists === '0'
	) {
		return false;
	}
	// show for all other scrip except equity
	return isIndex || isEquityFoScrip || isCurrencyScrip;
};

const scripDetailsService = {
	calculatePremiunDiscount,
	calculatePremiunDiscountPerc,
	formatLastTradedQuantity,
	mapScripLabelToTabVariant,
	mapMarketSegmentToId,
	mapExchangeToApiResponseKey,
	isIndexScrip,
	isCurrencyScrip,
	createLiveSnapshotVariant,
	mapApiSegmentToEquitySegment,
	mapEquitySegmentToSelectedExchange,
	mapEquitySegmentToApiSegment,
	calculateTabCtaVariant,
	isFoActivated,
	calculateFoTabCtaVariant,
	showOptionChainIcon,
	isOptionScrip,
	isUnderlyingData,
	isValuationVisible,
	formatMCap,
	shouldShowBuySellCta,
	mapScripDetailsToLegacyWaveScripDetails,
	isEquityFOScrip,
	userFutureCurrency,
	isCurrencyFoScrip,
	getScripDetailForeignKey,
	isEquityScrip,
	isTopOptionChainVisible,
	isETFScrip,
};

export default scripDetailsService;
